/** @jsxImportSource @emotion/react */
import type React from 'react';
import biometricsErrorSvg from '/biometrics-error.svg';
import { UniversalSpinner } from '../../../components/universal-spinner/universal-spinner';
import {
  AnimatedIconWrap,
  ButtonsCol,
  ErrorText,
  PrimaryButton,
  Screen,
  SecondaryButton,
  SpinnerWrap,
  SubtitleText,
  TitleText,
} from './biometric-error.styled';
import { useBiometricError } from './biometric-error.hooks';
import { VSpace } from '../../chat/settings/account/account-settings';
import type { LoginStepProps } from '../login-shell.types';

export const BiometricError: React.FC<LoginStepProps> = ({ go, data }) => {
  const isPreflight = !!data.biometricPreflightError;

  const { loading, error, retry, goEmail } = useBiometricError({
    onRetrySuccess: () => {
      go('biometric-login');
    },
    onEmailLogin: () => {
      go('email-login');
    },
  });

  return (
    <Screen data-testid="biometric-error-screen">
      <VSpace $h={187} />

      <AnimatedIconWrap
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.35, ease: [0.17, 0.67, 0.83, 0.67] }}
      >
        <AnimatedIconWrap
          animate={{
            rotate: [0, -2.2, 2.2, -1.2, 1.2, 0],
            scale: [1, 1.03, 1],
            filter: [
              'drop-shadow(0px 0px 0px rgba(255,138,138,0.0))',
              'drop-shadow(0px 0px 12px rgba(255,138,138,0.22))',
              'drop-shadow(0px 0px 0px rgba(255,138,138,0.0))',
            ],
          }}
          transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
        >
          <img
            style={{ width: '64px' }}
            src={biometricsErrorSvg}
            alt="Biometric verification error"
          />
        </AnimatedIconWrap>
      </AnimatedIconWrap>

      <VSpace $h={57} />
      <TitleText>Couldn’t verify it’s you.</TitleText>

      <VSpace $h={16} />
      <SubtitleText>
        {isPreflight
          ? data.biometricPreflightError
          : 'You can try again or log in with email instead.'}
      </SubtitleText>

      <VSpace $h={64} />

      <ButtonsCol>
        {!isPreflight && (
          <PrimaryButton onClick={retry} disabled={loading} data-testid="biometric-error-retry">
            Retry
          </PrimaryButton>
        )}

        <VSpace $h={16} />

        <SecondaryButton style={{ color: '#5E84ED', fontWeight: '600' }} onClick={goEmail} disabled={loading} data-testid="biometric-error-email">
          Use another log in method
        </SecondaryButton>

        {loading && (
          <SpinnerWrap data-testid="biometric-error-loading">
            <UniversalSpinner />
          </SpinnerWrap>
        )}

        {!loading && error && (
          <>
            <VSpace $h={12} />
            <ErrorText data-testid="biometric-error-error">{error}</ErrorText>
          </>
        )}
      </ButtonsCol>
    </Screen>
  );
};
